import React, { useCallback, useEffect, useRef } from "react";
import { EmailSubjectKeysType, SharedHistory } from "./useEmailEditorHistory";
import { isMacOs } from "react-device-detect";

export default function useSubjectHistory(
  subjectType: EmailSubjectKeysType,
  sharedHistoryRef?: React.MutableRefObject<SharedHistory>,
  onChange?: (e: any) => any
) {
  const ref = useRef<HTMLTextAreaElement>(null);
  const history = useRef<string[]>([]);
  const cursor = useRef<number>(0);

  const saveHistory = useCallback((value: string) => {
    history.current.splice(cursor.current + 1, 0, value);
    cursor.current++;
    history.current.length = cursor.current + 1;
    sharedHistoryRef.current?.save(subjectType);
  }, []);

  const undoHistory = useCallback(() => {
    if (!(ref.current && cursor.current)) return false;
    const lastState = history.current[cursor.current - 1];
    onChange({ target: { value: lastState } });
    cursor.current--;
    return true;
  }, []);

  const redoHistory = useCallback(() => {
    if (ref.current && cursor.current < history.current.length - 1) {
      const nextState = history.current[cursor.current + 1];
      onChange({ target: { value: nextState } });
      cursor.current++;
    }
  }, []);

  const handleCtrlZ = useCallback((e: KeyboardEvent) => {
    const keyCondition = (isMacOs ? e.metaKey : e.ctrlKey) && e.code === "KeyZ";
    if (keyCondition) {
      e.preventDefault();
      e.stopPropagation();
      sharedHistoryRef.current?.undo(subjectType);
    }
  }, []);

  const handleCtrlY = useCallback((e: KeyboardEvent) => {
    const keyConditionY =
      (isMacOs ? e.metaKey : e.ctrlKey) && e.code === "KeyY";

    const keyConditionZ =
      isMacOs && e.shiftKey && e.metaKey && e.code === "KeyZ";
    const keyCondition = keyConditionY || keyConditionZ;
    if (keyCondition) {
      e.preventDefault();
      e.stopPropagation();
      sharedHistoryRef.current?.redo(subjectType);
    }
  }, []);

  const focus = useCallback(() => {
    if (!ref.current) return;
    ref.current.focus();
  }, [ref.current]);

  useEffect(() => {
    if (!ref.current) return;
    ref.current.addEventListener("keydown", handleCtrlZ);
    ref.current.addEventListener("keydown", handleCtrlY, { capture: true });
    return () => {
      if (!ref.current) return;
      ref.current.removeEventListener("keydown", handleCtrlZ);
      ref.current.removeEventListener("keydown", handleCtrlY);
    };
  }, [ref.current, handleCtrlZ, handleCtrlY]);

  useEffect(() => {
    const initialValue = ref.current.value;
    history.current.push(initialValue);
  }, []);

  useEffect(() => {
    if (!ref.current) return;

    if (sharedHistoryRef.current) {
      sharedHistoryRef.current.instances[subjectType] = {
        undo: undoHistory,
        redo: redoHistory,
        save: saveHistory,
        focus,
      };
    }

    return () => {
      if (sharedHistoryRef.current) {
        delete sharedHistoryRef.current.instances[subjectType];
      }
    };
  }, [ref.current, focus, undoHistory, redoHistory, saveHistory]);

  return ref;
}
